import { Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { CommonService } from '../../../../shared/services/common.service';
import { DeviceService } from '../devices/device.service';
import { ColumnItem } from '../../../../shared/types';
import { CustomerSharedService } from '../../customer-shared.service';

import * as XLSX from 'xlsx';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-assets',
  templateUrl: './assets.component.html',
  styleUrl: './assets.component.scss'
})
export class AssetsComponent implements OnInit {
  @ViewChild('addAssetFile', { static: false }) addAssetFile!: ElementRef;
  // paginationConfig: NzTableQueryParams = { pageIndex: 1, pageSize: 20, sort: [], filter: [] };
  assetList$: any[] = [];
  filteredAssetList$: any[] = [];

  fileList: File[] = [];
  excelErr: string = '';
  excelErrLink: string = '';
  EXPECTED_HEADERS = ['asset_name', 'user_id', 'location_id', 'environment', 'make', 'model', 'year', 'tag', 'last_update_user']
  isSuccessMessage: boolean = false;

  apiCallLoaders = { isAssetsLoading: false };
  isConfirmLoading = false;

  isExcelModalVisible = false;
  isAssetModalVisible = false;

  quickFilter: 'all' | 'active' | 'inActive' = 'all';
  counts = { total_count: 0, active_count: 0, inactive_count: 0 };
  searchText: any;
  searchEnabled = false;

  isEditMode: boolean = false;
  editData: any = null;

  environmentOptions: any = [];
  customer_id: string;

  locationOptions: any[] = [];

  tagInputVisible = false;
  tagInputValue = '';

  // Asset form object
  assetFormData = {
    asset_id: null as number | null,
    asset_name: '',
    display_name: '',
    environment: '',
    make: '',
    model: '',
    year: '',
    tag: [] as string[],
    is_active: true
  };

  // Add new properties for tag handling
  listOfColumns: ColumnItem[] = [
    {
      name: 'ASSETS ID',
      sortOrder: null,
      sortFn: (a: any, b: any) => a.asset_id.localeCompare(b.asset_id),
      sortDirections: ['ascend', 'descend', null]
    },
    {
      name: '	ASSET NAME',
      sortOrder: null,
      sortFn: (a: any, b: any) => a.asset_name.localeCompare(b.asset_name),
      sortDirections: ['ascend', 'descend', null]
    }, {
      name: 'LOCATION',
      sortOrder: null,
      sortFn: (a: any, b: any) => a.display_name.localeCompare(b.display_name),
      sortDirections: ['ascend', 'descend', null]
    }, {
      name: 'LOCATION ID',
      sortOrder: null,
      sortFn: (a: any, b: any) => a.location_id.localeCompare(b.location_id),
      sortDirections: ['ascend', 'descend', null]
    }, {
      name: 'STATUS',
      sortOrder: null,
      sortFn: (a: any, b: any) => a.is_active.localeCompare(b.is_active),
      sortDirections: ['ascend', 'descend', null]
    },
  ]

  constructor(
    public route: ActivatedRoute,
    private fb: FormBuilder,
    public deviceService: DeviceService,
    public commonService: CommonService,
    public customerSharedSvc: CustomerSharedService,
    private ngZone: NgZone,
  ) {
    this.customer_id = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit() {
    this.filteredAssetList$ = [];
    this.getAssetByCustomerId();
    this.getLocationByCustomerId();
    this.getAllEnvironments();
  }

  // Add methods for tag handling
  handleClose(removedTag: string): void {
    this.assetFormData.tag = this.assetFormData.tag.filter(tag => tag !== removedTag);
  }

  sliceTagName(tag: string): string {
    const isLongTag = tag.length > 20;
    return isLongTag ? `${tag.slice(0, 20)}...` : tag;
  }

  showTagInput(): void {
    this.tagInputVisible = true;
    setTimeout(() => {
      const inputElement = document.getElementById('tagInput');
      if (inputElement) {
        inputElement.focus();
      }
    }, 10);
  }

  handleTagInputConfirm(): void {
    if (this.tagInputValue) {
      // Check if the input contains commas (multiple tags)
      if (this.tagInputValue.includes(',')) {
        // Split by comma, trim whitespace, and filter out empty strings
        const newTags = this.tagInputValue.split(',')
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0);

        // Add each tag that doesn't already exist
        newTags.forEach(tag => {
          if (this.assetFormData.tag.indexOf(tag) === -1) {
            this.assetFormData.tag = [...this.assetFormData.tag, tag];
          }
        });
      } else {
        // Single tag handling
        const tag = this.tagInputValue.trim();
        if (tag.length > 0 && this.assetFormData.tag.indexOf(tag) === -1) {
          this.assetFormData.tag = [...this.assetFormData.tag, tag];
        }
      }
    }
    this.tagInputValue = '';
    this.tagInputVisible = false;
  }

  // Add this helper method
  convertExcelDateToJsDate(excelDate: number): Date {
    // Excel's epoch starts from 1900-01-01
    const utcDays = Math.floor(excelDate - 25569);
    const utcValue = utcDays * 86400;
    const dateInfo = new Date(utcValue * 1000);
    return new Date(dateInfo.getFullYear(), dateInfo.getMonth(), dateInfo.getDate());
  }

  parseAssetDate(dateInput: any): Date | null {
    if (!dateInput) return null;

    // Check if it's a number (Excel date format)
    if (!isNaN(Number(dateInput)) && Number(dateInput) > 1000) {
      return this.convertExcelDateToJsDate(Number(dateInput));
    }
    // Check if it's a string date format (YYYY-MM-DD)
    else if (typeof dateInput === 'string' && dateInput.includes('-')) {
      try {
        const tempDate = new Date(dateInput);
        if (!isNaN(tempDate.getTime())) {
          return tempDate;
        }
      } catch (e) {
        console.error('Invalid date format:', dateInput);
      }
    }
    return null;
  }

  resetAssetForm(): void {
    this.assetFormData = {
      asset_id: null,
      asset_name: '',
      display_name: '',
      environment: '',
      make: '',
      model: '',
      year: '',
      tag: [],
      is_active: true
    };
    this.tagInputVisible = false;
    this.tagInputValue = '';
  }

  populateFormForEdit(data: any): void {
    // Parse the date
    const yearDate = this.parseAssetDate(data.year);

    // Debug the incoming data
    console.log('Edit asset data:', data);

    // Handle tags with proper type checking
    let tags: string[] = [];
    if (data.tag) {
      if (Array.isArray(data.tag)) {
        tags = [...data.tag];
      } else if (typeof data.tag === 'string') {
        // Check if it's a comma-separated string
        if (data.tag.includes(',')) {
          // Split by comma and trim whitespace
          tags = data.tag.split(',').map((tag: string) => tag.trim()).filter(Boolean);
        } else {
          try {
            // Try to parse if it's a JSON string
            const parsedTags = JSON.parse(data.tag);
            if (Array.isArray(parsedTags)) {
              tags = [...parsedTags];
            } else {
              tags = [data.tag]; // Use as a single tag
            }
          } catch (e) {
            // If parsing fails, treat as a single tag
            tags = [data.tag];
          }
        }
      }
    }


    this.assetFormData = {
      asset_id: data.asset_id,
      asset_name: data.asset_name,
      display_name: data.location_id,
      environment: data.environment,
      make: data.make,
      model: data.model,
      year: data.year,
      tag: tags,
      is_active: data.is_active
    };
  }

  addEditAsset(type: string, data?: any): void {
    // Set mode
    this.isEditMode = type === 'edit';
    this.editData = data;

    // First reset the form
    this.resetAssetForm();

    // Then populate form if in edit mode
    if (this.isEditMode && this.editData) {
      this.populateFormForEdit(this.editData);
      console.log('Tags in edit mode:', this.assetFormData.tag); // Add debug log
    }

    // Finally show the modal
    this.isAssetModalVisible = true;
  }

  validateAssetForm(): boolean {
    if (!this.assetFormData.asset_name || !this.assetFormData.display_name ||
      !this.assetFormData.environment || !this.assetFormData.make ||
      !this.assetFormData.model || !this.assetFormData.year) {
      this.commonService.message.error('Please fill in all required fields');
      return false;
    }
    return true;
  }

  prepareAssetPayload(): any {
    // Get user data from localStorage
    const admin_name = localStorage.getItem('admin_name') || '';
    const user_id = localStorage.getItem('user_id') || '';

    // Format the date to YYYY-MM-DD
    const formattedDate = this.assetFormData.year ? this.assetFormData.year : '';

    // Ensure tags are properly formatted
    const tags = Array.isArray(this.assetFormData.tag) ? this.assetFormData.tag : [];

    // Prepare the payload for the API
    const payload = {
      asset_id: this.assetFormData.asset_id,
      asset_name: this.assetFormData.asset_name,
      environment: this.assetFormData.environment,
      last_update_user: admin_name,
      location_id: this.assetFormData.display_name,
      make: this.assetFormData.make,
      model: this.assetFormData.model,
      tag: tags,
      user_id: user_id,
      year: formattedDate,
      is_active: this.assetFormData.is_active
    };

    console.log('Asset payload with tags:', payload);
    return payload;
  }

  downloadTemplate() {
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([this.EXPECTED_HEADERS]);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Asset list');
    XLSX.writeFile(wb, 'Asset_template.xlsx');
  }

  onFormSubmit() {
    // Validate the form
    if (!this.validateAssetForm()) {
      return;
    }

    this.isConfirmLoading = true;
    // Prepare API payload
    const assetData = this.prepareAssetPayload();

    // Call the appropriate API based on mode
    const apiCall = this.isEditMode ?
      this.deviceService.updateAsset(assetData) :
      this.deviceService.addAsset(assetData);

    // Make the API call
    apiCall.pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        if (response?.status === 200) {
          this.commonService.message.success(
            this.isEditMode ? 'Asset updated successfully' : 'Asset added successfully'
          );
          this.isAssetModalVisible = false;
          this.getAssetByCustomerId();
        } else {
          this.commonService.message.error(
            response?.message || (this.isEditMode ? 'Failed to update asset' : 'Failed to add asset')
          );
        }
        this.isConfirmLoading = false;
      },
      error: (error: any) => {
        this.commonService.message.error(
          this.isEditMode ? 'An error occurred while updating the asset' : 'An error occurred while adding the asset'
        );
        console.error('Error:', error);
        this.isConfirmLoading = false;
      }
    });
  }

  onFileChange(event: any): void {
    const file = event.target.files;
    const newFiles = Array.from(file);
    for (const file of newFiles as File[]) {
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const isValidType = validExtensions.some((ext) => fileName.endsWith(ext));
      if (!isValidType) {
        this.commonService.message.error(`File ${file.name} is not an allowed file type. Please upload excel file only.`);
        continue;
      }
      this.fileList.push(file);
    }
  }

  removeFile(index: number): void {
    this.fileList.splice(index, 1);
    this.addAssetFile.nativeElement.value = '';
    // this.excelErr = '';
    // this.excelErrLink = '';
    this.resetExcelState();
  }

  validateExcelFile(): boolean {
    if (this.fileList.length === 0) {
      this.commonService.message.error('Please select a file to upload');
      return false;
    }
    return true;
  }

  resetExcelState(): void {
    this.excelErr = '';
    this.excelErrLink = '';
    this.isSuccessMessage = false;
  }

  prepareExcelFormData(): FormData {
    const formData = new FormData();
    formData.append('xcelFile', this.fileList[0], this.fileList[0].name);
    return formData;
  }

  handleExcelUploadResponse(response: any): void {
    if (response?.status === 200) {
      // Check if there's a log file URL in the response
      if (response?.result) {
        this.excelErrLink = response.result;
      }
      // Set success message
      this.isSuccessMessage = true;
      this.excelErr = 'Excel file processed successfully';

      // Only close the modal if there are no errors or warnings
      if (!this.excelErrLink) {
        this.isExcelModalVisible = false;
        this.fileList = []; // Clear the file list
      }
      this.getAssetByCustomerId(); // Refresh the asset list
    } else {
      // Handle error responses
      this.isSuccessMessage = false;
      this.excelErr = response?.message || response?.error_message || 'Failed to process excel file';
      if (response?.log_file_url) {
        this.excelErrLink = response.log_file_url;
      }
      this.commonService.message.error(this.excelErr);
    }
  }

  handleExcelUploadError(error: any): void {
    this.isSuccessMessage = false;
    this.excelErr = error?.error?.message || 'An error occurred while uploading the excel file';
    if (error?.error?.log_file_url) {
      this.excelErrLink = error.error.log_file_url;
    }
    this.commonService.message.error(this.excelErr);
    console.error('Error uploading excel:', error);
  }
  onExcelSubmit() {
    // Validate file selection
    if (!this.validateExcelFile()) {
      return;
    }
    this.resetExcelState();
    this.isConfirmLoading = true;

    // Prepare and submit the form data
    const formData = this.prepareExcelFormData();
    this.deviceService.addAssetExcel(formData).pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        this.handleExcelUploadResponse(response);
        this.isConfirmLoading = false;
      },
      error: (error: any) => {
        this.handleExcelUploadError(error);
        this.isConfirmLoading = false;
      }
    });
  }

  // Add a helper method to download the log file
  downloadLogFile() {
    if (this.excelErrLink) {
      window.open(this.excelErrLink, '_blank');
    }
  }

  refreshHandler(): void {
    // Reset arrays
    this.assetList$ = [];
    this.filteredAssetList$ = [];
    this.quickFilter = 'all';
    this.searchText = '';
    this.searchEnabled = false;
    this.getAssetByCustomerId();
  }

  getAssetByCustomerId() {
    // Reset counts before fetching new data
    this.counts = { total_count: 0, active_count: 0, inactive_count: 0 };

    this.ngZone.runOutsideAngular(() => {
      this.deviceService.getAssetByCustomerId(this.customer_id).pipe(untilDestroyed(this)).subscribe(response => {
        if (response?.result && response?.status === 200) {
          this.assetList$ = response?.result;

          // Log the first asset to see its structure
          if (this.assetList$.length > 0) {
            console.log('Sample asset data from API:', this.assetList$[0]);
          }

          this.filteredAssetList$ = [...this.assetList$]
          this.assetList$.forEach(asset => {
            this.counts.total_count++
            if (asset?.is_active === true) this.counts.active_count++;
            if (asset?.is_active === false) this.counts.inactive_count++
          })
          this.ngZone.run(() => {
            this.apiCallLoaders.isAssetsLoading = false;
          });
        } else {
          this.ngZone.run(() => {
            this.apiCallLoaders.isAssetsLoading = false;
          });
        }
      });
    });
  }

  getSearchDetails(val: any): void {
    let searchText = val.trim().toLowerCase();
    this.filteredAssetList$ = this.assetList$.filter(asset => {
      return (asset.asset_name && asset.asset_name.toLowerCase().includes(searchText)) ||
        (asset.display_name && asset.display_name.toLowerCase().includes(searchText))
    });
  }

  getLocationByCustomerId() {
    this.commonService.getLocationList(Number(this.customer_id)).subscribe({
      next: (response: any) => {
        const locations = Array.isArray(response?.data) ? response.data : [];
        this.locationOptions = locations;
      },
      error: (err) => {
        console.error('Error fetching locations', err);
        this.commonService.message.error('Failed to fetch locations');
      }
    })
  }

  filterData(): void {
    if (this.quickFilter === 'all') {
      this.filteredAssetList$ = this.assetList$;
    } else if (this.quickFilter === 'active') {
      this.filteredAssetList$ = this.assetList$.filter(asset => asset.is_active);
    } else if (this.quickFilter === 'inActive') {
      this.filteredAssetList$ = this.assetList$.filter(asset => !asset.is_active);
    }
  }

  handleCancel(type: string): void {
    if (type === 'excel') {
      this.removeFile(0);
      this.resetExcelState();
      this.isExcelModalVisible = false;
    } else if (type === 'asset') {
      this.isAssetModalVisible = false;
    }
  }

  // Add method to get environment options
  getAllEnvironments(): void {
    this.apiCallLoaders.isAssetsLoading = true;
    this.deviceService.getAllEnvironments(this.customer_id).pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        if (response?.data) {
          this.environmentOptions = response.data;
          this.apiCallLoaders.isAssetsLoading = false;
        } else {
          this.apiCallLoaders.isAssetsLoading = false;
          this.commonService.message.error('No environment data received');
        }
      },
      error: (error: any) => {
        this.apiCallLoaders.isAssetsLoading = false;
        this.commonService.message.error('An error occurred while fetching environments');
        console.error('Error fetching environments:', error);
      }
    });
  }

  deleteAsset(asset_id: number) {
    this.deviceService.deleteAsset(asset_id).pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        if (response?.status === 200) {
          this.commonService.message.success('Asset deleted successfully');
          this.getAssetByCustomerId();
        } else {
          this.commonService.message.error(response?.message);
        }
      }
    });
  }
}
